import { appRoutes } from '../../configuration/constants'
import {
  normalizeLabelWithCount,
  getPassengerLabelKeysFromCode
} from '../../configuration/utilities'
import { formatPriceByLanguage } from '../configuration/utilities'
import { selectFerryRouteSelectionsState } from '../ferryRouteSelections/selectors'
import { selectPassengerSelections } from '../passengerSelections/selectors'
import {
  selectDeparturePets,
  selectReturnPets
} from '../passengerSelections/selectors'
import {
  selectDepartureVehicle,
  selectReturnVehicle
} from '../vehicleSelections/selectors'
import selectVehicleFormType from '../selectors/selectVehicleFormType'
import { selectActiveCrossings } from '../activeCrossings/selectors'
import { selectCrossingData } from '../crossings/selectors'
import { createSelector } from 'reselect'
import { selectLabels } from '../configuration/selectors'
import { selectPaymentConfirmationData } from '../paymentConfirmation/selectors'
import { selectActiveAccommodations } from '../activeAccommodations/selectors'
import { selectActiveModifyBooking } from '../modifyBooking/selectors'
import { selectLanguage } from '../session/selectors'
import { get } from 'lodash'

import {
  getDateValue,
  getTotalFromPriceLines
} from '../../components/summary/utilities'
import { ENABLE_EXTRAS } from '../../configuration/constants'
import { getFaresString } from '../../configuration/utilities'

const state = state => state

export const selectPricingBreakdown = createSelector(state, allState => {
  const crossingState = selectActiveCrossings(allState)
  const labels = selectLabels(allState)
  const language = selectLanguage(allState)
  const paymentConfirmationData = selectPaymentConfirmationData(allState)
  const activeModifyBooking = selectActiveModifyBooking(allState)
  const departurePets = selectDeparturePets(allState)
  const returnPets = selectReturnPets(allState)

  if (paymentConfirmationData) {
    return {
      total: paymentConfirmationData?.amount || 0
    }
  }

  const activeFerryRoutes = selectFerryRouteSelectionsState(allState)
  /*
   * Price priorities are paymentConfirmation.data // this is a finalized and paid trip
   * bookingData.data // this is either prior to payment OR on modification,
   * then Active crossings
   * otherwise we make an estimate  (this is only needed on two steps., passengers and vehicles.)
   * */
  const activeAccommodations = selectActiveAccommodations(allState)
  const breakdown = Object.entries(crossingState).reduce(
    (obj, [directionKey, direction]) => {
      const matchingRouteData =
        activeFerryRoutes[
          directionKey === 'departure' ? 'departureRoute' : 'returnRoute'
        ]
      const directionTotal = get(
        direction,
        'bookingPrice.bookingPrice.totalPrice',
        0
      )
      const miscFees = get(direction, 'bookingPrice.bookingPrice.fees') || []

      obj[directionKey].total = directionTotal
      obj[directionKey].miscFees = miscFees

      const passengers = get(
        direction,
        'bookingPrice.bookingPrice.resources.passengers'
      )
      const passengerTaxesAndLevies = get(
        direction,
        'bookingPrice.bookingPrice.resources.passengerTaxesAndLevies',
        {}
      )
      obj[directionKey].passengerTaxesAndLevies = passengerTaxesAndLevies

      if (passengers && passengers.length) {
        obj[directionKey].passengers = passengers.reduce(
          (passengerObj, passengerType) => {
            const matchingPassengersDetails =
              matchingRouteData && matchingRouteData.passengerTypes
                ? matchingRouteData.passengerTypes
                : []

            const matchingPassenger = matchingPassengersDetails.find(
              type => type.type === passengerType.type
            )

            const passengerLabels = getPassengerLabelKeysFromCode(
              matchingPassenger?.code
            )

            if (!passengerObj[passengerType.type]) {
              passengerObj[passengerType.type] = {
                ...passengerType,
                displayName:
                  normalizeLabelWithCount({
                    labelKey: passengerLabels.default,
                    pluralLabelKey: passengerLabels.plural,
                    language,
                    count: 1,
                    showCountWhenOne: true,
                    labels
                  }) || labels.passenger,
                count: 1
              }
            } else {
              const newCount = passengerObj[passengerType.type].count + 1

              passengerObj[passengerType.type] = {
                ...passengerObj[passengerType.type],
                count: newCount,
                displayName: normalizeLabelWithCount({
                  labelKey: passengerLabels.default,
                  pluralLabelKey: passengerLabels.plural,
                  language,
                  count: newCount,
                  showCountWhenOne: true,
                  labels
                })
              }
            }
            return passengerObj
          },
          {}
        )
      }

      const vehicles = get(
        direction,
        'bookingPrice.bookingPrice.resources.vehicle'
      )
      const vehicleTaxesAndLevies = get(
        direction,
        'bookingPrice.bookingPrice.resources.vehicleTaxesAndLevies',
        {}
      )
      obj[directionKey].vehicleTaxesAndLevies = vehicleTaxesAndLevies

      if (vehicles && vehicles.length) {
        obj[directionKey].vehicles = reduceResource(vehicles)
      }

      const accommodations = get(
        direction,
        'bookingPrice.bookingPrice.resources.accommodations'
      )
      const accommodationTaxesAndLevies = get(
        direction,
        'bookingPrice.bookingPrice.resources.accommodationTaxesAndLevies',
        {}
      )
      obj[
        directionKey
      ].accommodationTaxesAndLevies = accommodationTaxesAndLevies

      if (activeAccommodations?.[directionKey]) {
        obj[directionKey].accommodations = activeAccommodations?.[directionKey]

        for (const accommodation of Object.values(
          activeAccommodations?.[directionKey]
        )) {
          obj[directionKey].total =
            +obj[directionKey].total +
            +accommodation.count * +accommodation?.details?.price
        }
      }

      if (accommodations && accommodations.length) {
        obj[directionKey].accommodations = reduceResource(accommodations)
      }
      obj.total += +obj[directionKey].total

      return obj
    },
    {
      total: 0,
      departure: {},
      return: {}
    }
  )
  // we have everything we need, no need to build estimates.
  if (
    breakdown?.departure?.passengers &&
    (activeFerryRoutes.activeRouteForm !== 'RT' ||
      breakdown?.return?.passengers)
  ) {
    if (activeModifyBooking?.paid) {
      breakdown.paid = Number(activeModifyBooking.paid)
      breakdown.totalAfterPaid =
        Number(breakdown.total) - Number(activeModifyBooking.paid)
    }
    return breakdown
  }

  const crossingsData = selectCrossingData(allState)

  if (
    !Object.keys(crossingsData).length ||
    (crossingsData.departure && crossingsData.departure.loading) ||
    (crossingsData.return && crossingsData.return.loading)
  ) {
    return {
      total: 0,
      loading: true
    }
  }

  const passengerSelections = selectPassengerSelections(allState)
  const departureVehicleSelections = selectDepartureVehicle(allState)
  const returnVehicleSelections = selectReturnVehicle(allState)
  const vehicleFormType = selectVehicleFormType(allState)
  // if we have yet to select a departure crossing build a departure estimate
  if (!crossingState.departure) {
    breakdown.departure.isEstimate = true

    if (
      crossingsData &&
      crossingsData.departure &&
      crossingsData.departure.passengerBestRates
    ) {
      const directionEstimate = renderDirectionEstimate({
        crossingsData: crossingsData.departure,
        passengerSelections: passengerSelections.departure,
        activeFerryRoutes: activeFerryRoutes['departureRoute'],
        vehicle:
          crossingsData?.departure?.resourceBestRates?.[
            departureVehicleSelections?.vehicleType?.resourceCode
          ],
        labels,
        language,
        pets: departurePets
      })
      breakdown.departure = {
        ...breakdown.departure,
        ...directionEstimate
      }

      if (directionEstimate?.total) {
        breakdown.total += directionEstimate?.total
      }
    }
  }

  // if we have yet to select a return crossing and are booking a return trip, build a return estimate
  if (!crossingState.return && activeFerryRoutes.activeRouteForm === 'RT') {
    breakdown.return.isEstimate = true

    const selectedVehicle =
      vehicleFormType === 'VEH-DUP' || vehicleFormType === 'VELO'
        ? crossingsData?.return?.resourceBestRates?.[
            departureVehicleSelections?.vehicleType?.resourceCode
          ]
        : crossingsData?.return?.resourceBestRates?.[
            returnVehicleSelections?.vehicleType?.resourceCode
          ]
    if (
      crossingsData &&
      crossingsData.return &&
      crossingsData.return.passengerBestRates
    ) {
      const directionEstimate = renderDirectionEstimate({
        crossingsData: crossingsData.return,
        passengerSelections: passengerSelections.duplicatePassengerQuantities
          ? passengerSelections.departure
          : passengerSelections.return,
        activeFerryRoutes: activeFerryRoutes['returnRoute'],
        vehicle: selectedVehicle,
        labels,
        language,
        pets: returnPets
      })
      breakdown.return = {
        ...breakdown.return,
        ...directionEstimate
      }

      if (directionEstimate?.total) {
        breakdown.total += directionEstimate?.total
      }
    }
  }

  if (activeModifyBooking?.paid) {
    breakdown.paid = Number(activeModifyBooking.paid)
    breakdown.totalAfterPaid =
      Number(breakdown.total) - Number(activeModifyBooking.paid)
  }

  // if we have yet to select a return crossing AND we have selected a return trip build an estimate
  return breakdown
})

function renderDirectionEstimate({
  crossingsData,
  passengerSelections,
  activeFerryRoutes,
  vehicle,
  labels,
  language,
  pets
}) {
  const tripEstimate = {
    total: 0
  }

  if (
    passengerSelections &&
    activeFerryRoutes?.passengerTypes?.length &&
    crossingsData?.passengerBestRates
  ) {
    tripEstimate.passengers = Object.entries(passengerSelections).reduce(
      (obj, [passengerType, passengerCount]) => {
        if (passengerCount === 0) {
          return obj
        }
        const matchingPassengerType = activeFerryRoutes.passengerTypes.find(
          type => type.code === passengerType
        )

        if (matchingPassengerType?.type) {
          const passengerLabels = getPassengerLabelKeysFromCode(
            matchingPassengerType.code
          )
          const normalizedLabelWithCount = normalizeLabelWithCount({
            labelKey: passengerLabels.default,
            pluralLabelKey: passengerLabels.plural,
            language,
            count: passengerCount,
            showCountWhenOne: true,
            labels
          })
          const bestRate = crossingsData.passengerBestRates
          obj[matchingPassengerType.type] = {
            ...matchingPassengerType,
            count: passengerCount,
            price: bestRate?.[matchingPassengerType.type] || 0,
            description: labels.passengers,
            displayName:
              normalizedLabelWithCount || labels[matchingPassengerType.code]
          }
          tripEstimate.total += bestRate?.[matchingPassengerType.type]
            ? +passengerCount * +bestRate?.[matchingPassengerType.type]
            : 0
        }

        return obj
      },
      {}
    )
    if (vehicle) {
      tripEstimate.vehicles = {
        [vehicle.resourceCode]: { ...vehicle, count: 1 }
      }
      tripEstimate.total += +vehicle.price || 0
    }

    if (pets) {
      tripEstimate.passengers.PT = {
        resourceCode: 'PT',
        price: 0,
        description: 'Pets',
        discount: 0,
        netPrice: 0,
        netDiscount: 0,
        displayName:
          normalizeLabelWithCount({
            labelKey: 'petLabel',
            pluralLabelKey: 'pets',
            language,
            count: pets,
            showCountWhenOne: true,
            labels
          }) || labels.petLabel,
        count: 1
      }
    }
  }

  return tripEstimate
}

export function getRouteBreakdown(
  route,
  labels,
  language,
  passengers,
  modifyBookingRoute,
  vehicleLength
) {
  if (!route) {
    return null
  }

  const {
    departurePriceData,
    passengerAddonPriceLines,
    vehicleAddonPriceLines,
    amendmentFeePriceLines
  } = route

  if (!departurePriceData || !labels || !language) {
    if (process.env.REACT_APP_VERB_ENV !== 'production') {
      console.warn('Malformed getRouteBreakdown parameters')
    }
    return null
  }

  const { adult, senior, child, infant, student, pet } = passengers
  const vehicleDetails = route.vehicles?.booked?.[0]

  const accommodationDisplayStrings = route.accommodationPriceLines?.length
    ? route.accommodationPriceLines.map(
        accommodation =>
          `${+accommodation.amount} - ${labels[accommodation.resourceCode]}`
      )
    : null
  const accommodationTaxSections = (route.accommodationPriceLines || [])
    .map(line => {
      const { tax, taxPercentage, resourceCode } = line

      if (tax && +tax > 0) {
        return {
          value: `${labels[resourceCode]} (${+taxPercentage}%)`,
          cost: formatPriceByLanguage(tax, language),
          rawCost: +tax
        }
      } else {
        return null
      }
    })
    .filter(x => !!x)
  const addonFeeSections = [
    ...(passengerAddonPriceLines || []),
    ...(vehicleAddonPriceLines || [])
  ].map(line => {
    const { price, resourceCode } = line
    return {
      value: labels[resourceCode],
      cost: formatPriceByLanguage(price, language),
      rawCost: +price
    }
  })
  const amendmentFeeSections = (amendmentFeePriceLines || []).map(line => {
    const { price, resourceCode } = line
    return {
      value: labels[resourceCode] || labels.amendmentFee,
      cost: formatPriceByLanguage(price, language),
      rawCost: +price
    }
  })
  // NOTE(ebarrett): taxesAndLevies is a CTMA-only construct, consists of a object map of tax key to cost.
  //  For NFL and MAI, it's returned as an empty array instead, thus the check below
  const leviesMap =
    typeof departurePriceData.taxesAndLevies === 'object' &&
    !Array.isArray(departurePriceData.taxesAndLevies)
      ? departurePriceData.taxesAndLevies
      : {}
  const leviesSections = Object.entries(leviesMap).map(
    ([taxItemKey, price]) => {
      return {
        value: labels[taxItemKey] || labels.amendmentFee,
        cost: formatPriceByLanguage(price, language),
        rawCost: +price
      }
    }
  )

  const getPriceLinesRawCost = lines =>
    lines && lines.length ? getTotalFromPriceLines(lines) : 0
  const formatPriceLines = lines =>
    formatPriceByLanguage(getPriceLinesRawCost(lines), language)
  // as of writing this comment only MAI uses the label dateTime and it will be undefined for other clients.
  const dateSection = {
    label: labels.dateTime || labels.date,
    value: getDateValue(route, labels, language),
    cost: null,
    iconType: 'calendar',
    changeRoute: appRoutes.ferryRoutes.pathname
  }
  const passengersSection = {
    label: labels.passengers,
    value: getFaresString({
      labels,
      language,
      adults: adult,
      seniors: senior,
      children: child,
      infants: infant,
      students: student,
      pets: pet
    }),
    cost: formatPriceLines(route.passengerPriceLines),
    rawCost: getPriceLinesRawCost(route.passengerPriceLines),
    iconType: 'passenger',
    changeRoute: appRoutes.passengerQuantities.pathname
  }
  const vehicleSection = {
    label: labels.vehicle,
    value: vehicleDetails
      ? `${labels[vehicleDetails.resourceCode]}${
          +vehicleLength > 21 ? ` (${vehicleLength} ${labels.feet})` : ''
        }`
      : labels.noVehicle,
    cost: formatPriceLines(route.vehiclePriceLines),
    rawCost: getPriceLinesRawCost(route.vehiclePriceLines),
    iconType: 'vehicle',
    changeRoute: appRoutes.vehicle.pathname
  }
  const accommodationsSection = {
    label: labels.extras,
    value: accommodationDisplayStrings?.length
      ? accommodationDisplayStrings.join(', ')
      : labels.noKennelsSelected || 'On-Leash/In Vehicle',
    cost: formatPriceLines(route.accommodationPriceLines),
    rawCost: getPriceLinesRawCost(route.accommodationPriceLines),
    iconType: ENABLE_EXTRAS ? 'extras' : 'accommodations',
    changeRoute: appRoutes.accommodations.pathname
  }
  const discountSection =
    +departurePriceData.discount > 0
      ? {
          label: labels.discounts,
          value: '',
          cost: formatPriceByLanguage(departurePriceData.discount),
          rawCost: departurePriceData.discount
        }
      : null
  const taxesAndFeesSections = [
    ...accommodationTaxSections,
    ...addonFeeSections,
    ...amendmentFeeSections,
    ...leviesSections
  ]

  const tripTotal = +departurePriceData.total
  const alreadyPaid = modifyBookingRoute
    ? +modifyBookingRoute.departurePriceData.total
    : false
  const balanceDue = alreadyPaid ? tripTotal - alreadyPaid : tripTotal

  return {
    dateSection,
    passengersSection,
    vehicleSection,
    accommodationsSection,
    discountSection,
    taxesAndFeesSections,
    tripTotal,
    alreadyPaid,
    balanceDue
  }
}

function reduceResource(resources) {
  return resources.reduce((obj, resource) => {
    if (!obj[resource.resourceCode]) {
      obj[resource.resourceCode] = {
        ...resource,
        count: 1
      }
    } else {
      obj[resource.resourceCode].count += 1
    }

    return obj
  }, {})
}
